import { DialogComponent } from '@theme/dialog';
import { Wishlist, WishlistUpdateEvent } from '@theme/wishlist';

/**
 * A custom element that displays a wishlist drawer/modal.
 * Shows all wishlist items and allows users to remove them or add to cart.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} wishlistItems - The container for wishlist items.
 * @property {HTMLElement} emptyMessage - The empty wishlist message.
 * @property {HTMLElement} itemCount - The item count display.
 * @property {HTMLElement} shareFooter - The share footer element.
 * @property {HTMLElement} shareResult - The share result container.
 * @property {HTMLInputElement} shareUrl - The share URL input.
 * @property {HTMLElement} copyFeedback - The copy feedback message.
 *
 * @extends {DialogComponent<Refs>}
 */
class WishlistDrawer extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  /**
   * Shows the dialog and loads wishlist items.
   */
  showDialog() {
    super.showDialog();
    this.loadWishlistItems();
  }

  /**
   * Handles wishlist update events.
   * @param {WishlistUpdateEvent} event
   */
  onWishlistUpdate = (event) => {
    if (this.refs.dialog?.open) {
      this.loadWishlistItems();
    }
  };

  /**
   * Loads and renders wishlist items.
   */
  async loadWishlistItems() {
    const items = Wishlist.getItems();
    const wishlistItems = this.refs.wishlistItems;
    const emptyMessage = this.refs.emptyMessage;
    const itemCount = this.refs.itemCount;

    if (itemCount) {
      itemCount.textContent = String(items.length);
    }

    if (items.length === 0) {
      if (wishlistItems) wishlistItems.innerHTML = '';
      if (emptyMessage) emptyMessage.classList.remove('hidden');
      this.classList.add('wishlist-drawer--empty');
      this.updateShareFooter(false);
      return;
    }

    if (emptyMessage) emptyMessage.classList.add('hidden');
    this.classList.remove('wishlist-drawer--empty');
    this.updateShareFooter(true);

    // Fetch product data for each item using the handle
    try {
      const productPromises = items.map((item) => {
        // If we have a handle, use it; otherwise skip (old data without handle)
        if (!item.handle) {
          return Promise.resolve(null);
        }
        return fetch(`/products/${item.handle}.json`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);
      });

      const productDataList = await Promise.all(productPromises);
      const validProducts = productDataList.filter(Boolean);

      if (wishlistItems) {
        wishlistItems.innerHTML = validProducts
          .map((data) => {
            const quantity = Wishlist.getQuantity(data.product.id);
            return `
          <div class="wishlist-item" data-product-id="${data.product.id}">
            <a href="/products/${data.product.handle}" class="wishlist-item__link">
              <div class="wishlist-item__image">
                ${
                  data.product.image
                    ? `<img src="${data.product.image.src}" alt="${data.product.title}" loading="lazy" width="80" height="80">`
                    : '<div class="wishlist-item__placeholder"></div>'
                }
              </div>
              <div class="wishlist-item__details">
                <h4 class="wishlist-item__title">${data.product.title}</h4>
                <p class="wishlist-item__price">${this.formatPrice(data.product.variants[0]?.price)}</p>
              </div>
            </a>
            <div class="wishlist-item__actions">
              <div class="wishlist-item__quantity">
                <button
                  type="button"
                  class="wishlist-item__qty-btn"
                  on:click="/decreaseQuantity?productId=${data.product.id}"
                  aria-label="Decrease quantity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
                <input
                  type="number"
                  class="wishlist-item__qty-input"
                  value="${quantity}"
                  min="1"
                  max="99"
                  aria-label="Quantity"
                  data-product-id="${data.product.id}"
                >
                <button
                  type="button"
                  class="wishlist-item__qty-btn"
                  on:click="/increaseQuantity?productId=${data.product.id}"
                  aria-label="Increase quantity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              </div>
              <button
                class="wishlist-item__remove"
                on:click="/removeItem?productId=${data.product.id}"
                aria-label="Remove ${data.product.title} from wishlist"
                title="Remove from wishlist"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        `;
          })
          .join('');

        // Add event listeners for quantity inputs
        wishlistItems.querySelectorAll('.wishlist-item__qty-input').forEach((input) => {
          input.addEventListener('change', this.onQuantityInputChange);
        });
      }
    } catch (error) {
      console.error('Error loading wishlist items:', error);
    }
  }

  /**
   * Formats a price value for display.
   * @param {string|number} price - The price as decimal string (e.g., "29.99").
   * @returns {string} The formatted price.
   */
  formatPrice(price) {
    if (!price) return '';
    // Shopify Product JSON API returns price as decimal string, not cents
    const amount = parseFloat(price);
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
    }).format(amount);
  }

  /**
   * Removes an item from the wishlist.
   * @param {{ productId: string }} data - The product ID to remove.
   * @param {MouseEvent} event
   */
  removeItem(data, event) {
    event.preventDefault();
    event.stopPropagation();
    Wishlist.removeProduct(data.productId);
  }

  /**
   * Increases the quantity of an item in the wishlist.
   * @param {{ productId: string }} data - The product ID.
   * @param {MouseEvent} event
   */
  increaseQuantity(data, event) {
    event.preventDefault();
    event.stopPropagation();
    const currentQty = Wishlist.getQuantity(data.productId);
    Wishlist.updateQuantity(data.productId, currentQty + 1);
  }

  /**
   * Decreases the quantity of an item in the wishlist.
   * @param {{ productId: string }} data - The product ID.
   * @param {MouseEvent} event
   */
  decreaseQuantity(data, event) {
    event.preventDefault();
    event.stopPropagation();
    const currentQty = Wishlist.getQuantity(data.productId);
    Wishlist.updateQuantity(data.productId, currentQty - 1);
  }

  /**
   * Handles direct quantity input changes.
   * @param {Event} event
   */
  onQuantityInputChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const productId = input.dataset.productId;
    let value = parseInt(input.value) || 1;
    value = Math.max(1, Math.min(value, 99));
    input.value = String(value);
    if (productId) {
      Wishlist.updateQuantity(productId, value);
    }
  };

  /**
   * Updates the visibility of the share footer.
   * @param {boolean} hasItems - Whether the wishlist has items.
   */
  updateShareFooter(hasItems) {
    const shareFooter = this.refs.shareFooter;
    if (!shareFooter) return;

    // Show share footer if has items (works for both logged in and guest users)
    if (hasItems) {
      shareFooter.classList.remove('hidden');
    } else {
      shareFooter.classList.add('hidden');
    }
  }

  /**
   * Generates a shareable wishlist link.
   * Items with quantity > 1 are formatted as "handle:quantity"
   */
  generateShareLink() {
    const items = Wishlist.getItems();
    if (items.length === 0) return;

    const customerData = window.wishlistCustomerData || {};

    // Format items as "handle:quantity" (omit quantity if 1)
    const itemStrings = items
      .filter((item) => item.handle)
      .map((item) => {
        const qty = item.quantity || 1;
        return qty > 1 ? `${item.handle}:${qty}` : item.handle;
      });

    // Build share URL
    const baseUrl = window.location.origin;
    const sharePage = customerData.sharePageUrl || '/pages/shared-wishlist';
    const params = new URLSearchParams();

    params.set('items', itemStrings.join(','));

    if (customerData.customerName) {
      params.set('from', customerData.customerName);
    }

    if (customerData.customerId) {
      params.set('cid', customerData.customerId);
    }

    const shareUrl = `${baseUrl}${sharePage}?${params.toString()}`;

    // Show the share result
    const shareResult = this.refs.shareResult;
    const shareUrlInput = this.refs.shareUrl;

    if (shareResult && shareUrlInput) {
      shareUrlInput.value = shareUrl;
      shareResult.classList.remove('hidden');
      shareUrlInput.select();
    }
  }

  /**
   * Copies the share link to clipboard.
   */
  async copyShareLink() {
    const shareUrlInput = this.refs.shareUrl;
    const copyFeedback = this.refs.copyFeedback;

    if (!shareUrlInput) return;

    try {
      await navigator.clipboard.writeText(shareUrlInput.value);

      if (copyFeedback) {
        copyFeedback.classList.remove('hidden');
        setTimeout(() => {
          copyFeedback.classList.add('hidden');
        }, 2000);
      }
    } catch (err) {
      // Fallback for older browsers
      shareUrlInput.select();
      document.execCommand('copy');

      if (copyFeedback) {
        copyFeedback.classList.remove('hidden');
        setTimeout(() => {
          copyFeedback.classList.add('hidden');
        }, 2000);
      }
    }
  }
}

if (!customElements.get('wishlist-drawer')) {
  customElements.define('wishlist-drawer', WishlistDrawer);
}
